/**
 * ONE definition of what the Customers search box searches.
 *
 * Board #207: the placeholder promises name, email, phone and address. The server query
 * searched name/first/last/company/email/phone and NO address at all, and it stripped `()`
 * out of the term before an ilike — so "(541) 978-9236" became "541 978-9236", which matches
 * nothing, while the bare digits "5419789236" worked. A user typing the number exactly as the
 * app itself displays it got zero results.
 *
 * Behind that sat a SECOND, different rule in pages/Customers.js — `customer.phone?.includes(term)`
 * plus `street_address` only — so even a row the server returned could be filtered back out on
 * render, and the CSV export (which reuses the client predicate) disagreed with the table.
 * Two rules for one question is the recurring defect on this page; both sides now import from
 * here.
 *
 * PHONE. Every one of the 228 stored phone numbers is E.164 with no punctuation (verified live
 * 2026-09-17: 228 of 228 match ^\+[0-9]+$). So a phone-like query is reduced to its digits and
 * matched as a substring: "(541) 978-9236" -> "5419789236" -> matches "+15419789236", and a
 * partial "978-9236" -> "9789236" matches too.
 *
 * A query only takes the phone branch if it is ENTIRELY digits and phone punctuation. That
 * guard matters: "123 River Rd" contains digits, and treating it as a phone would turn a street
 * search into a broad numeric match — the brief calls this out specifically.
 *
 * Matching semantics are INFERRED, not a settled product decision. Jobber documents client
 * phone and address search; punctuation-insensitive substring is the industry-standard reading
 * of the promise this placeholder already makes.
 */

/** Characters people type in a phone number that are not digits. */
const PHONE_PUNCTUATION = /[\s()+\-.]/g;

/**
 * Address columns to search. BOTH families deliberately: `customers` carries the legacy
 * `street_address` / `city` / `state` / `zip_code` AND the canonical `billing_*` set, and
 * different rows populate different ones (CLAUDE.md §15, check:aliases). Searching one family
 * makes a customer unfindable by the address actually shown on their own card.
 */
export const CUSTOMER_ADDRESS_FIELDS = [
  'street_address',
  'city',
  'state',
  'zip_code',
  'billing_address_line_1',
  'billing_address_line_2',
  'billing_city',
  'billing_state',
  'billing_zip_code',
];

/** Text columns that identify a customer. */
export const CUSTOMER_TEXT_FIELDS = [
  'name',
  'first_name',
  'last_name',
  'company_name',
  'email',
];

export const CUSTOMER_PHONE_FIELDS = ['phone', 'mobile_phone'];

/**
 * Interpret a raw search box value.
 *
 * @returns {{ raw: string, text: string, digits: string, isPhoneLike: boolean, isEmpty: boolean }}
 *   `text` is safe to interpolate into a PostgREST filter: the characters that would break the
 *   `or(...)` grammar or act as wildcards are removed rather than escaped, matching what this
 *   query already did.
 */
export const parseCustomerSearch = (raw) => {
  const trimmed = (raw || '').trim();
  const digits = trimmed.replace(/\D/g, '');
  // Entirely digits and phone punctuation, and containing at least one digit.
  const isPhoneLike = digits.length > 0 && trimmed.replace(PHONE_PUNCTUATION, '').replace(/\d/g, '') === '';
  return {
    raw: trimmed,
    text: trimmed.replace(/[,()%*]/g, ' ').trim(),
    digits,
    isPhoneLike,
    isEmpty: trimmed === '',
  };
};

/**
 * #5640, operator ruling 2026-10-15 ("industry standard or better"): a text search is split into
 * WORDS (on spaces and hyphens) and every word must appear somewhere, in any order — so
 * "Smith John" finds John Smith and "smith jones" finds Smith-Jones. It used to match the whole
 * typed string as one substring. A phone-like query is still matched as one run of digits.
 */
export const customerSearchWords = (text) =>
  String(text || '').toLowerCase().split(/[\s-]+/).filter(Boolean);

/**
 * The PostgREST `or(...)` arguments for a search term — ONE PER WORD, each to be applied with
 * its own `.or()` (PostgREST ANDs them) — or null when there is nothing to filter. Phone columns
 * are only included for a phone-like query, so an address containing a house number does not
 * match every customer whose number shares those digits.
 */
export const buildCustomerSearchFilter = (raw) => {
  const { text, digits, isPhoneLike, isEmpty } = parseCustomerSearch(raw);
  if (isEmpty) return null;
  const textFields = [...CUSTOMER_TEXT_FIELDS, ...CUSTOMER_ADDRESS_FIELDS];

  if (isPhoneLike) {
    const clauses = CUSTOMER_PHONE_FIELDS.map((field) => `${field}.ilike.%${digits}%`);
    if (text) for (const field of textFields) clauses.push(`${field}.ilike.%${text}%`);
    return [clauses.join(',')];
  }

  const words = customerSearchWords(text);
  if (!words.length) return null;
  return words.map((word) => textFields.map((field) => `${field}.ilike.%${word}%`).join(','));
};

/**
 * The client-side twin, applied to an already-loaded row. Must classify the same way as
 * buildCustomerSearchFilter or a row the server returned gets filtered out on render.
 */
export const customerMatchesSearch = (customer, raw) => {
  const { text, digits, isPhoneLike, isEmpty } = parseCustomerSearch(raw);
  if (isEmpty || !customer) return true;

  if (isPhoneLike) {
    const hit = CUSTOMER_PHONE_FIELDS.some((field) =>
      String(customer[field] || '').replace(/\D/g, '').includes(digits)
    );
    if (hit) return true;
  }

  if (!text) return false;
  const fields = [...CUSTOMER_TEXT_FIELDS, ...CUSTOMER_ADDRESS_FIELDS];
  const words = isPhoneLike ? [text.toLowerCase()] : customerSearchWords(text);
  if (!words.length) return false;
  // Tags are a client-only enrichment; the server cannot filter on them, so this is the one
  // place the two sides legitimately differ — it can only ever ADD a match, never remove one.
  return words.every((word) =>
    fields.some((field) => String(customer[field] || '').toLowerCase().includes(word))
    || Boolean(customer.autoTags?.some((tag) => String(tag).toLowerCase().includes(word)))
  );
};
